import { Activity, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCounterReadingsDue } from '@/features/counter-readings/queries';

/**
 * Dashboard widget — list of equipments whose latest reading on at
 * least one configured counter is older than the declared frequency.
 * Each row links to the equipment detail page where the user can
 * record the reading.
 */
export function CountersDueWidget() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useCounterReadingsDue();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity aria-hidden className="h-4 w-4" />
          {t('counter.due.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : isError ? (
          <p className="text-sm text-red-700">{t('counter.due.loadError')}</p>
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('counter.due.empty')}</p>
        ) : (
          <ul className="divide-y">
            {data.map((row) => (
              <li
                key={`${row.equipmentId}:${row.field}`}
                className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
              >
                <Link
                  to={`/equipment/${row.equipmentId}`}
                  className="flex flex-1 items-center justify-between gap-2 hover:underline"
                >
                  <div>
                    <p className="text-sm font-medium">{row.equipmentName}</p>
                    <p className="text-xs text-muted-foreground">
                      {t(`counter.field.${row.field}`, { defaultValue: row.field })}{' '}
                      ·{' '}
                      {row.daysSinceLast === null
                        ? t('counter.due.neverRead')
                        : t('counter.due.daysSince', { count: row.daysSinceLast })}
                    </p>
                  </div>
                  <ChevronRight aria-hidden className="h-4 w-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
